import React from 'react';
import { Heading, List, ListItem, Stack, Text } from '@origin-digital/ods-core';
import {
  CONTENT_SEPARATOR,
  DETAILS_CONTENT_KEYS,
  KeyDetailsItem,
  PLAN_DETAILS_PLAN_PERIOD,
  PLAN_DETAILS_PLAN_PERIOD_BENEFIT,
  StaticPerkAccordionType,
} from '../../typings';
import { getKeyDetailsContentIcon } from '../utils/PlanMetaHelper';

export const ShiftPlanKeyDetailsModal = (
  <Stack data-id="shift-plan-key-details-info-modal">
    <Heading variant="h3">About energy rates</Heading>
    <Stack space="xxsmall">
      <Text weight="medium">Variable rates</Text>
      <Text>
        A variable rate means what you pay for energy could go up or down. Origin may change prices
        – we'll always let you know. We consider all kinds of factors when setting and changing our
        prices, like changes in our costs and conditions in the energy market overall.
      </Text>
    </Stack>
    <Stack space="xxsmall">
      <Text weight="medium">Time of use (Electricity only)</Text>
      <Text>
        Your tariff will change to the time of use tariff above. This means what you pay will depend
        on the time of day, and will differ from your current tariff type and rates. If you're
        already on a time of use tariff, your peak and off-peak times, and rates will also change.
      </Text>
    </Stack>
    <Stack space="xxsmall">
      <Text weight="medium">Fixed rates</Text>
      <Text>
        A fixed (or guaranteed) rate won’t change over your energy plan period. The rates you sign
        up for today won’t change for 12 months.
      </Text>
    </Stack>
  </Stack>
);

const shiftRatesDetail: KeyDetailsItem = {
  contentKey: DETAILS_CONTENT_KEYS.Rates,
  contentValue: 'Variable',
  modalContent: ShiftPlanKeyDetailsModal,
  contentIcon: getKeyDetailsContentIcon(DETAILS_CONTENT_KEYS.Rates),
  contentDescription: 'Variable rates + time of use',
};

const shiftExitFeesDetail: KeyDetailsItem = {
  contentKey: DETAILS_CONTENT_KEYS.ExitFees,
  contentValue: 'No',
  contentIcon: getKeyDetailsContentIcon(DETAILS_CONTENT_KEYS.ExitFees),
  contentDescription: 'No exit fees. Leave anytime.',
};

export const shiftOngoingKeyDetails: KeyDetailsItem[] = [
  shiftRatesDetail,
  {
    contentKey: DETAILS_CONTENT_KEYS.BenefitPeriod,
    contentValue:
      PLAN_DETAILS_PLAN_PERIOD.Ongoing + CONTENT_SEPARATOR + PLAN_DETAILS_PLAN_PERIOD_BENEFIT,
    contentIcon: getKeyDetailsContentIcon(DETAILS_CONTENT_KEYS.BenefitPeriod),
    contentDescription: 'Ongoing contract',
  },
  shiftExitFeesDetail,
];

export const shiftVicKeyDetails: KeyDetailsItem[] = [
  shiftRatesDetail,
  shiftExitFeesDetail,
  {
    contentKey: DETAILS_CONTENT_KEYS.BenefitPeriod,
    contentValue: '12 months' + CONTENT_SEPARATOR + PLAN_DETAILS_PLAN_PERIOD_BENEFIT,
    contentIcon: getKeyDetailsContentIcon(DETAILS_CONTENT_KEYS.BenefitPeriod),
    contentDescription: '12 month contract',
  },
];

const shiftPerkIcon =
  'https://www.originenergy.com.au/cdn/images/originenergy/image/upload/icons/ic-LocalOffer_promote.svg';

export const nightShiftPerk: StaticPerkAccordionType = {
  accordionIcon: shiftPerkIcon,
  accordionSummary: <Text weight="medium">Charge your EV from $5 between 12-6am*</Text>,
  accordionBody: (
    <Stack space="medium">
      <Text>
        Currently, the variable off-peak electricity rate from 12-6am every night is 8c/kWh.
      </Text>
      <List space="xxsmall">
        <ListItem>Make the most of off peak electricity rates at night to charge your EV</ListItem>
        <ListItem>Great for scheduling EV charging or appliances to run overnight</ListItem>
        <ListItem>Suits those who charge their EV from 12-6am</ListItem>
        <ListItem>
          If you have a controlled load, it will be charged at controlled load rates.
        </ListItem>
      </List>
      <Text>You’ll need a smart meter to sign up for Origin Shift.</Text>
      <Text>
        *$5 EV full charge estimate is based on a continuous electricity charge rate of 8c/kWh
        powering a Tesla Model Y or Tesla Model 3 with a useable 57.5 kWh battery.
      </Text>
    </Stack>
  ),
  accordionDataId: 'shift-plan-perk',
  expanded: false,
};

export const weekdayShiftPerk: StaticPerkAccordionType = {
  accordionIcon: shiftPerkIcon,
  accordionSummary: (
    <Text weight="medium">Off-peak electricity rates weekdays between 9am-5pm</Text>
  ),
  accordionBody: (
    <Stack space="medium">
      <Text>
        Currently, the variable off-peak rate from 9am-5pm Monday to Friday is half-price compared
        to peak rates on this plan.*
      </Text>
      <List space="xxsmall">
        <ListItem>Suits those who use the most electricity between 9am – 5pm weekdays</ListItem>
        <ListItem>Great for running appliances from 9am to 5pm weekdays</ListItem>
        <ListItem>Suits those who can shift electricity usage to 9am – 5pm weekdays</ListItem>
        <ListItem>
          If you have a controlled load, it will be charged at controlled load rates.
        </ListItem>
      </List>
      <Text>You’ll need a smart meter to sign up for Origin Shift.</Text>
      <Text>*Off-peak and peak rates may change, we’ll let you know if they do.</Text>
    </Stack>
  ),
  accordionDataId: 'shift-plan-perk',
  expanded: false,
};
